import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Euro, TrendingUp, Users, Eye, Check, X, Settings } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCommissions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const driverIdFromUrl = searchParams.get('driverId');
  
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [commissionRate, setCommissionRate] = useState(10);
  const [newRate, setNewRate] = useState(10);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverDetails, setDriverDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchCommissionData();
    // Si un driverId est dans l'URL, ouvrir automatiquement ses détails
    if (driverIdFromUrl) {
      viewDriverDetails(driverIdFromUrl);
    }
  }, [driverIdFromUrl]);

  const fetchCommissionData = async () => {
    try {
      setLoading(true);
      const [summaryRes, rateRes] = await Promise.all([
        axios.get(`${API}/api/admin/commissions/summary`),
        axios.get(`${API}/api/admin/commission-rate`)
      ]);
      
      setSummary(summaryRes.data);
      setCommissionRate(rateRes.data.commissionRate);
      setNewRate(rateRes.data.commissionRate);
    } catch (error) {
      console.error('Error fetching commission data:', error);
      toast.error('Erreur de chargement des commissions');
    } finally {
      setLoading(false);
    }
  };

  const updateCommissionRate = async () => {
    try {
      await axios.put(`${API}/api/admin/commission-rate`, {
        commissionRate: parseFloat(newRate)
      });
      
      setCommissionRate(newRate);
      setShowRateDialog(false);
      toast.success(`Taux de commission mis à jour: ${newRate}%`);
      fetchCommissionData();
    } catch (error) {
      console.error('Error updating commission rate:', error);
      toast.error('Erreur de mise à jour du taux');
    }
  };

  const viewDriverDetails = async (driverId) => {
    try {
      setLoadingDetails(true);
      const response = await axios.get(`${API}/api/admin/drivers/${driverId}/commissions`);
      setDriverDetails(response.data);
      setSelectedDriver(driverId);
    } catch (error) {
      console.error('Error fetching driver details:', error);
      toast.error('Erreur de chargement des détails');
    } finally {
      setLoadingDetails(false);
    }
  };

  const markCommissionPaid = async (bookingId) => {
    try {
      await axios.put(`${API}/api/bookings/${bookingId}/commission-paid`, {
        paid: true
      });
      
      toast.success('Commission marquée comme payée');
      // Refresh details
      if (selectedDriver) {
        viewDriverDetails(selectedDriver);
      }
      fetchCommissionData();
    } catch (error) {
      console.error('Error marking commission paid:', error);
      toast.error('Erreur de mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="text-gold hover:text-amber-400"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour
            </Button>
            <h1 className="text-4xl font-bold text-gold">💰 Commissions Chauffeurs</h1>
          </div>
          
          <Button
            onClick={() => setShowRateDialog(true)}
            className="bg-gold hover:bg-amber-600 text-black"
          >
            <Settings className="mr-2 h-4 w-4" />
            Modifier le taux ({commissionRate}%)
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-gray-800/50 border-gold/30">
            <CardHeader>
              <CardTitle className="text-gold flex items-center gap-2">
                <Euro className="h-5 w-5" />
                Total Dû
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {summary?.overallTotals?.totalCommissionDue?.toFixed(2) || 0}€
              </div>
              <p className="text-gray-400 text-sm mt-2">Commission totale due</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-green-500/30">
            <CardHeader>
              <CardTitle className="text-green-500 flex items-center gap-2">
                <Check className="h-5 w-5" />
                Total Payé
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {summary?.overallTotals?.totalCommissionPaid?.toFixed(2) || 0}€
              </div>
              <p className="text-gray-400 text-sm mt-2">Déjà encaissé</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-800/50 border-red-500/30">
            <CardHeader>
              <CardTitle className="text-red-500 flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Total Impayé
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {summary?.overallTotals?.totalCommissionUnpaid?.toFixed(2) || 0}€
              </div>
              <p className="text-gray-400 text-sm mt-2">À encaisser</p>
            </CardContent>
          </Card>
        </div>

        {/* Drivers Table */}
        <Card className="bg-gray-800/50 border-gold/30">
          <CardHeader>
            <CardTitle className="text-gold flex items-center gap-2">
              <Users className="h-5 w-5" />
              Commissions par Chauffeur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700">
                  <TableHead className="text-gold">Chauffeur</TableHead>
                  <TableHead className="text-gold">Courses</TableHead>
                  <TableHead className="text-gold">Total Dû</TableHead>
                  <TableHead className="text-gold">Payé</TableHead>
                  <TableHead className="text-gold">Impayé</TableHead>
                  <TableHead className="text-gold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {summary?.drivers?.map((driver) => (
                  <TableRow key={driver.driverId} className="border-gray-700">
                    <TableCell className="font-medium text-white">
                      {driver.driverName}
                    </TableCell>
                    <TableCell className="text-gray-300">
                      {driver.totalRides}
                    </TableCell>
                    <TableCell className="text-gray-300">
                      {driver.totalCommissionDue.toFixed(2)}€
                    </TableCell>
                    <TableCell className="text-green-500">
                      {driver.totalCommissionPaid.toFixed(2)}€
                    </TableCell>
                    <TableCell className="text-red-500 font-bold">
                      {driver.totalCommissionUnpaid.toFixed(2)}€
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={() => viewDriverDetails(driver.driverId)}
                        className="bg-gold/20 hover:bg-gold/30 text-gold"
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Détails
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(!summary?.drivers || summary.drivers.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-gray-400 py-8">
                      Aucune commission enregistrée
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Rate Dialog */}
      <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
        <DialogContent className="bg-gray-800 text-white border-gold/30">
          <DialogHeader>
            <DialogTitle className="text-gold">Modifier le Taux de Commission</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300">Taux actuel: {commissionRate}%</Label>
            </div>
            <div>
              <Label htmlFor="newRate" className="text-gray-300">
                Nouveau taux (%)
              </Label>
              <Input
                id="newRate"
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                className="bg-gray-700 border-gold/30 text-white mt-2"
              />
              <p className="text-xs text-gray-400 mt-1">
                Ce taux sera appliqué aux nouvelles courses terminées
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowRateDialog(false)}
                className="border-gray-600 text-gray-300"
              >
                Annuler
              </Button>
              <Button
                onClick={updateCommissionRate}
                className="bg-gold hover:bg-amber-600 text-black"
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Driver Details Dialog */}
      <Dialog open={!!selectedDriver} onOpenChange={() => setSelectedDriver(null)}>
        <DialogContent className="bg-gray-800 text-white border-gold/30 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gold">
              Historique Détaillé - {driverDetails?.driverId}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="text-center py-8">Chargement...</div>
          ) : driverDetails && (
            <div className="space-y-6 mt-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-700/50 p-4 rounded-lg">
                  <div className="text-sm text-gray-400">Total Courses</div>
                  <div className="text-2xl font-bold text-white">{driverDetails.totalRides}</div>
                </div>
                <div className="bg-gray-700/50 p-4 rounded-lg">
                  <div className="text-sm text-gray-400">Dû Total</div>
                  <div className="text-2xl font-bold text-gold">{driverDetails.totalCommissionDue.toFixed(2)}€</div>
                </div>
                <div className="bg-gray-700/50 p-4 rounded-lg">
                  <div className="text-sm text-gray-400">Impayé</div>
                  <div className="text-2xl font-bold text-red-500">{driverDetails.totalCommissionUnpaid.toFixed(2)}€</div>
                </div>
              </div>

              {/* Bookings List */}
              <div>
                <h3 className="text-lg font-semibold text-gold mb-4">Courses Effectuées</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {driverDetails.bookings.map((booking) => (
                    <div key={booking.id} className="bg-gray-700/30 p-4 rounded-lg border border-gray-600">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="text-sm text-gray-400">{booking.date} à {booking.time}</div>
                          <div className="font-medium text-white mt-1">
                            {booking.pickup} → {booking.dropoff || 'Service horaire'}
                          </div>
                          <div className="text-sm text-gray-400 mt-1">
                            Prix: {booking.estimatedPrice}€ | Commission ({booking.commissionRate}%): {booking.commissionAmount?.toFixed(2)}€
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {booking.commissionPaid ? (
                            <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                              <Check className="h-3 w-3 mr-1" />
                              Payé
                            </Badge>
                          ) : (
                            <>
                              <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
                                <X className="h-3 w-3 mr-1" />
                                Impayé
                              </Badge>
                              <Button
                                size="sm"
                                onClick={() => markCommissionPaid(booking.id)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                              >
                                Marquer payé
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {driverDetails.bookings.length === 0 && (
                    <div className="text-center text-gray-400 py-8">
                      Aucune course terminée
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCommissions;
